/**
 * Design Tokens for TAMS Application
 * Modern Corporate Theme - Professional Blue Palette
 *
 * Usage:
 * - Import tokens in theme.ts, App.css, and component styles
 * - Use consistent spacing, colors, and typography across the app
 */

export const tokens = {
  colors: {
    // Primary palette - Professional Blues
    primary: {
      50: '#EBF5FF',
      100: '#D6EBFF',
      200: '#ADD6FF',
      300: '#70B8FF',
      400: '#3D9EFF',
      500: '#1A7EF8',  // Main primary for highlights
      600: '#0A5DC2',  // Main primary for buttons/actions
      700: '#084999',
      800: '#063670',
      900: '#042347',
    },

    // Neutral/Gray scale - For text, backgrounds, borders
    neutral: {
      50: '#F8FAFC',   // Page background
      100: '#F1F5F9',  // Card hover, subtle backgrounds
      200: '#E2E8F0',  // Borders, dividers
      300: '#CBD5E1',  // Disabled states
      400: '#94A3B8',  // Placeholder text
      500: '#64748B',  // Secondary text
      600: '#475569',  // Body text
      700: '#334155',  // Headings
      800: '#1E293B',  // Primary text
      900: '#0F172A',  // High emphasis text
    },

    // Semantic colors - Status indicators
    success: {
      main: '#10B981',
      light: '#D1FAE5',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3C7',
      dark: '#D97706',
      contrastText: '#000000',
    },
    error: {
      main: '#EF4444',
      light: '#FEE2E2',
      dark: '#DC2626',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#3B82F6',
      light: '#DBEAFE',
      dark: '#2563EB',
      contrastText: '#FFFFFF',
    },

    // Alarm severity colors (TAMS specific)
    alarm: {
      predictive: {
        text: '#B97400',
        background: '#FEF3C7',
        border: '#F59E0B',
      },
      failure: {
        text: '#B80000',
        background: '#FEE2E2',
        border: '#EF4444',
      },
      maintenanceDeficiency: {
        text: '#5C6505',
        background: '#F6FAAE',
        border: '#84CC16',
      },
      maintenanceOverdue: {
        text: '#5A459E',
        background: '#E5D9F9',
        border: '#8B5CF6',
      },
    },

    // Background colors
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
      elevated: '#FFFFFF',
      subtle: '#F1F5F9',
    },
  },

  // Spacing scale (8px base)
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
  },

  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',    // 4px - small elements
    md: '0.5rem',     // 8px - buttons, inputs
    lg: '0.75rem',    // 12px - cards, modals
    xl: '1rem',       // 16px - large cards
    '2xl': '1.5rem',  // 24px - hero sections
    full: '9999px',   // Pills, avatars
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
    // Card shadows
    card: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    cardHover: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    // Dropdown/Modal shadows
    dropdown: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    modal: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  },

  // Typography
  typography: {
    fontFamily: {
      sans: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.625,
    },
    letterSpacing: {
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
    },
  },

  // Transitions
  transitions: {
    fast: '150ms ease',
    normal: '250ms ease',
    slow: '350ms ease',
    // Specific transitions
    colors: '150ms ease',
    shadow: '200ms ease',
    transform: '200ms ease',
  },

  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },

  // Layout dimensions
  layout: {
    sidebarWidth: '72px',
    sidebarWidthExtended: '240px',
    headerHeight: '56px',
    maxContentWidth: '1440px',
    containerPadding: '1rem',
  },

  // Breakpoints (for reference, MUI handles this)
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },
};

// Helper to get CSS variable string
export const cssVar = (name: string) => `var(--${name})`;

// Export individual token groups for convenience
export const { colors, spacing, borderRadius, shadows, typography, transitions, layout } = tokens;

export default tokens;
